import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import type {
  DeliveryAttemptSummary,
  DeliveryOperationsEvidence,
  DeliveryOperationsPolicy,
} from "@reminder-tracker/backend-contracts";
import { deriveDeliveryHealth, type DeliveryHealthSignal } from "@reminder-tracker/domain";
import { getDeliveryOperationsEvidence } from "../../backend/functions";
import {
  AdministrationAuditDetails,
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationSection,
  AdministrationStatusSummary,
} from "../../ui/administration-surface";
import {
  exactUtcTimestamp,
  formatAdministrationTime,
  formatAdministrationWindow,
} from "../../ui/administration-time";

const labels: Record<DeliveryHealthSignal, string> = {
  "missing-configuration": "Required settings missing",
  "credential-rejected": "Credential rejection recorded",
  "configuration-not-checked": "Configuration not checked",
  incomplete: "Evidence incomplete",
  "no-evidence": "No outcomes recorded",
  "repeated-failures": "Repeated failures recorded",
  "failures-recorded": "Failures recorded",
  "unknown-outcomes": "Unknown outcomes recorded",
  "no-failures-recorded": "No failures recorded",
};
function timestamp(value: number | null): string {
  return value === null ? "No evidence recorded" : formatAdministrationTime(value);
}
function mode(policy: DeliveryOperationsPolicy): string {
  return policy === null ? "Not configured" : policy.mode === "disabled" ? "Disabled" : "Test only";
}
function Outcomes({ summary }: Readonly<{ summary: DeliveryAttemptSummary }>): React.JSX.Element {
  if (summary.counts === null)
    return <p>The read limit was reached. Partial counts are not shown as totals.</p>;
  return (
    <dl className="scheduler-details">
      <div>
        <dt>Processing</dt>
        <dd>{summary.counts.processing}</dd>
      </div>
      <div>
        <dt>Accepted by provider</dt>
        <dd>{summary.counts.accepted}</dd>
      </div>
      <div>
        <dt>Provider handoff confirmed</dt>
        <dd>{summary.counts.providerHandoff}</dd>
      </div>
      <div>
        <dt>Failed</dt>
        <dd>{summary.counts.failed}</dd>
      </div>
      <div>
        <dt>Unknown</dt>
        <dd>{summary.counts.unknown}</dd>
      </div>
    </dl>
  );
}
export function DeliveryEvidenceView({
  evidence,
}: Readonly<{ evidence: DeliveryOperationsEvidence }>): React.JSX.Element {
  const channels = [
    {
      name: "Email invitations",
      policy: evidence.policies.email,
      configuration: evidence.configuration.email,
      summary: evidence.emailInvitations,
      hour: evidence.warningHour.emailInvitations,
    },
    {
      name: "Email reminders",
      policy: evidence.policies.email,
      configuration: evidence.configuration.email,
      summary: evidence.emailReminders,
      hour: evidence.warningHour.emailReminders,
    },
    {
      name: "Android push",
      policy: evidence.policies.androidPush,
      configuration: evidence.configuration.androidPush,
      summary: evidence.androidPush,
      hour: evidence.warningHour.androidPush,
    },
  ];
  const channelSignals = channels.map((channel) => ({
    ...channel,
    signal: deriveDeliveryHealth({
      complete: channel.hour.state === "complete",
      accepted: (channel.hour.counts?.accepted ?? 0) + (channel.hour.counts?.providerHandoff ?? 0),
      failed: channel.hour.counts?.failed ?? 0,
      unknown: channel.hour.counts?.unknown ?? 0,
      credentialRejected: channel.hour.credentialRejected,
      configuration: channel.configuration,
    }),
  }));
  const actionSignals: readonly DeliveryHealthSignal[] = [
    "missing-configuration",
    "credential-rejected",
    "repeated-failures",
  ];
  const healthySignals: readonly DeliveryHealthSignal[] = ["no-failures-recorded"];
  const actionCount = channelSignals.filter((channel) =>
    actionSignals.includes(channel.signal),
  ).length;
  const healthyCount = channelSignals.filter((channel) =>
    healthySignals.includes(channel.signal),
  ).length;
  const monitorCount = channelSignals.length - actionCount - healthyCount;
  return (
    <>
      <AdministrationStatusSummary
        items={[
          {
            label: "Needs action",
            tone: "action",
            value: actionCount,
            title: "Channels with an action signal",
            description: "Missing settings, rejected credentials or repeated failures.",
          },
          {
            label: "Monitor",
            tone: "monitor",
            value: monitorCount,
            title: "Channels with uncertain evidence",
            description: "Incomplete, absent, failed or unknown evidence needs interpretation.",
          },
          {
            label: "Healthy",
            tone: "healthy",
            value: healthyCount,
            title: "Channels with no failures recorded",
            description: "This does not prove customer receipt, reading or completion.",
          },
        ]}
      />
      <AdministrationImpactPanel
        label="Customer impact"
        title={
          actionCount > 0
            ? `${actionCount} ${actionCount === 1 ? "channel needs" : "channels need"} operator action`
            : monitorCount > 0
              ? "Delivery evidence needs monitoring"
              : "No channel failures are recorded in this evidence window"
        }
        description="Provider acceptance or handoff does not prove that a customer saw a reminder. Review the channel evidence before changing a sending policy."
        action={
          <a className="secondary-action" href="#delivery-channel-evidence">
            Review channel evidence
          </a>
        }
        evidence={
          <p>
            Status counts classify the three external channel rows shown below; they are not counts
            of customers, messages or completed reminders.
          </p>
        }
        tone={actionCount > 0 ? "action" : monitorCount > 0 ? "monitor" : "healthy"}
      />
      <p>
        Evidence window:{" "}
        {formatAdministrationWindow(
          evidence.startAtEpochMilliseconds,
          evidence.endAtEpochMilliseconds,
        )}
        .
      </p>
      <p>
        Configuration presence does not verify credentials. Provider acceptance or handoff does not
        prove inbox placement, phone display, reading or completion.
      </p>
      <table className="timeline delivery-evidence" id="delivery-channel-evidence">
        <caption>Sending policy, current settings and observed outcomes</caption>
        <thead>
          <tr>
            <th>Service</th>
            <th>Current signal</th>
            <th>Sending policy</th>
            <th>Settings</th>
            <th>Evidence</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">In-app</th>
            <td data-label="Current signal">Available without an external provider</td>
            <td data-label="Sending policy">Permanently enabled</td>
            <td data-label="Settings">No external provider required</td>
            <td data-label="Evidence">
              {evidence.inApp.available === null
                ? "Evidence incomplete"
                : `${evidence.inApp.available} availability records`}
              <br />
              Last record: {timestamp(evidence.inApp.latestEvidenceAtEpochMilliseconds)}
            </td>
          </tr>
          {channelSignals.map((channel) => (
            <tr key={channel.name}>
              <th scope="row">{channel.name}</th>
              <td data-label="Current signal">
                <strong className={`channel-signal channel-signal-${channel.signal}`}>
                  {labels[channel.signal]}
                </strong>
                <br />
                <small>
                  {channel.hour.counts === null
                    ? "Totals withheld"
                    : `${channel.hour.counts.failed} failed / ${channel.hour.counts.accepted + channel.hour.counts.providerHandoff + channel.hour.counts.failed} known terminal attempts; ${channel.hour.counts.unknown} unknown`}
                </small>
              </td>
              <td data-label="Sending policy">{mode(channel.policy)}</td>
              <td data-label="Settings">
                {channel.configuration === "present"
                  ? "Required settings present"
                  : "Required settings missing"}
              </td>
              <td data-label="Evidence">
                <details>
                  <summary>Inspect {channel.name.toLowerCase()} outcomes</summary>
                  <Outcomes summary={channel.summary} />
                  <p>
                    Last evidence: {timestamp(channel.summary.latestEvidenceAtEpochMilliseconds)}
                  </p>
                </details>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <details className="inline-details">
        <summary>How current signals are calculated</summary>
        <p>
          The current signal uses the one-hour window from{" "}
          {timestamp(evidence.warningStartAtEpochMilliseconds)} to{" "}
          {timestamp(evidence.endAtEpochMilliseconds)}. Repeated failures means at least 5 failures
          and 20% of known terminal attempts; unknown outcomes are excluded from that denominator.
        </p>
        <p>
          Provider figures count retained attempts by their current state and last update, not every
          historical provider event. In-app figures count retained availability records by creation,
          not views or reads. Incomplete or unavailable evidence is not a zero result. Inspecting
          this page never sends a message.
        </p>
      </details>
      <AdministrationAuditDetails summary="Exact evidence interval and correlation reference">
        <dl className="audit-register">
          <div>
            <dt>Window start (UTC)</dt>
            <dd>
              <time dateTime={exactUtcTimestamp(evidence.startAtEpochMilliseconds)}>
                {exactUtcTimestamp(evidence.startAtEpochMilliseconds)}
              </time>
            </dd>
          </div>
          <div>
            <dt>Window end (UTC, excluded)</dt>
            <dd>
              <time dateTime={exactUtcTimestamp(evidence.endAtEpochMilliseconds)}>
                {exactUtcTimestamp(evidence.endAtEpochMilliseconds)}
              </time>
            </dd>
          </div>
          <div>
            <dt>Correlation reference</dt>
            <dd className="masked">{evidence.correlationId}</dd>
          </div>
        </dl>
      </AdministrationAuditDetails>
    </>
  );
}

export function ProtectedDeliveryEvidence(): React.JSX.Element {
  const [correlationId] = useState(() => `corr_${crypto.randomUUID().replaceAll("-", "")}`);
  const [window, setWindow] = useState<"24-hours" | "7-days">("24-hours");
  const [endAtEpochMilliseconds, setEnd] = useState(() => Date.now());
  useEffect(() => {
    const interval = globalThis.setInterval(() => {
      if (document.visibilityState === "visible") setEnd(Date.now());
    }, 60_000);
    return () => globalThis.clearInterval(interval);
  }, []);
  const evidence = useQuery(getDeliveryOperationsEvidence, {
    correlationId,
    window,
    endAtEpochMilliseconds,
  });
  return (
    <>
      <AdministrationPageHeader
        title="Delivery services"
        purpose="See whether reminder channels are ready and where customers may be affected."
      />
      <AdministrationSection
        id="delivery-evidence-heading"
        title="Channel readiness"
        description="Sending policy, configuration and observed outcomes remain separate."
        className="scheduler-assurance"
      >
        <label>
          Evidence window{" "}
          <select
            value={window}
            onChange={(event) => setWindow(event.target.value === "7-days" ? "7-days" : "24-hours")}
          >
            <option value="24-hours">Last 24 hours</option>
            <option value="7-days">Last 7 days</option>
          </select>
        </label>
        {evidence === undefined ? (
          <p role="status">Reading delivery evidence…</p>
        ) : (
          <DeliveryEvidenceView evidence={evidence} />
        )}
      </AdministrationSection>
    </>
  );
}
